import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    if (target) target.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    if (this.refs.zoomDialogComponent) {
      this.refs.zoomDialogComponent.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
        signal,
      });
    }

    requestAnimationFrame(() => {
      this.#ensurePrimaryImageResolution();
    });

    window.addEventListener('pageshow', this.#handlePageShow, { signal: this.#controller.signal });
  }

  #controller = new AbortController();
  #variantUpdateToken = 0;
  #pendingFrame = 0;
  #pendingUpgrade = null;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#cancelPendingWork();
  }

  #cancelPendingWork() {
    if (this.#pendingFrame) {
      cancelAnimationFrame(this.#pendingFrame);
      this.#pendingFrame = 0;
    }
    if (this.#pendingUpgrade) {
      this.#pendingUpgrade.cancelled = true;
      this.#pendingUpgrade = null;
    }
  }

  #scheduleFrame(callback) {
    if (this.#pendingFrame) cancelAnimationFrame(this.#pendingFrame);
    this.#pendingFrame = requestAnimationFrame(() => {
      this.#pendingFrame = 0;
      callback();
    });
  }

  #handleVariantUpdate = (event) => {
    const source = event.detail?.data?.html;
    const resource = event.detail?.resource;

    if (!source) return;

    const token = ++this.#variantUpdateToken;
    this.#cancelPendingWork();

    if (resource) {
      this.#previewVariantMedia(resource);
    }

    if (resource && this.#switchToVariantMedia(resource)) {
      return;
    }

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    if (resource && this.dataset.hideVariants === 'true' && this.#galleryMatchesVariant(newMediaGallery, resource)) {
      return;
    }

    this.#scheduleFrame(() => {
      if (token !== this.#variantUpdateToken) return;
      morph(this, newMediaGallery);
      this.#ensurePrimaryImageResolution();
    });
  };

  #getPrimaryImage() {
    return (
      this.querySelector('.media-gallery__grid .product-media-container:first-child img.product-media__image') ||
      this.querySelector('.media-gallery__grid img.product-media__image') ||
      this.querySelector('slideshow-slide[aria-hidden="false"] img.product-media__image') ||
      this.querySelector('slideshow-slide:first-child img.product-media__image') ||
      this.querySelector('.product-media img.product-media__image')
    );
  }

  #getPreviewVariantUrl(variant) {
    const previewSrc = variant?.featured_media?.preview_image?.src;
    if (previewSrc) {
      try {
        const url = new URL(previewSrc, window.location.origin);
        url.searchParams.set('width', '1400');
        return url.toString();
      } catch {
        return previewSrc;
      }
    }
    return null;
  }

  #mediaIdsFrom(root, selector) {
    return Array.from(root.querySelectorAll(selector))
      .map((el) => el.getAttribute('data-media-id'))
      .filter(Boolean);
  }

  #galleryMatchesVariant(newGallery, variant) {
    const mediaId = String(variant?.featured_media?.id || '');
    if (!mediaId) return false;

    const hasGrid = Boolean(this.querySelector('.media-gallery__grid'));
    const currentSelector = hasGrid
      ? '.media-gallery__grid .product-media[data-media-id]'
      : 'slideshow-component slideshow-slide .product-media[data-media-id]';
    const newHasGrid = Boolean(newGallery.querySelector('.media-gallery__grid'));
    const newSelector = newHasGrid
      ? '.media-gallery__grid .product-media[data-media-id]'
      : 'slideshow-component slideshow-slide .product-media[data-media-id]';

    const current = this.#mediaIdsFrom(this, currentSelector);
    const next = this.#mediaIdsFrom(newGallery, newSelector);
    if (!current.length || current.length !== next.length) return false;
    if (current[0] !== mediaId || next[0] !== mediaId) return false;
    return current.slice(1).join() === next.slice(1).join();
  }

  #handlePageShow = () => {
    requestAnimationFrame(() => {
      this.#ensurePrimaryImageResolution();
    });
  };

  #applyImageSource(img, url, alt) {
    if (!(img instanceof HTMLImageElement) || !url) return false;

    img.removeAttribute('srcset');
    img.setAttribute('data_max_resolution', url);
    img.dataset.highResApplied = 'false';
    if (img.src !== url) img.src = url;
    if (alt) img.alt = alt;
    if (img.clientWidth) img.sizes = `${Math.ceil(img.clientWidth)}px`;
    return true;
  }

  #upgradeImageToMaxResolution(img) {
    if (!(img instanceof HTMLImageElement)) return;

    const maxRes = img.getAttribute('data_max_resolution');
    if (!maxRes || img.dataset.highResApplied === 'true') return;

    if ((img.currentSrc || img.src) === maxRes) {
      img.dataset.highResApplied = 'true';
      return;
    }

    if (this.#pendingUpgrade) {
      this.#pendingUpgrade.cancelled = true;
      this.#pendingUpgrade = null;
    }

    const token = this.#variantUpdateToken;
    const pending = { cancelled: false };
    this.#pendingUpgrade = pending;

    const loader = new Image();
    loader.onload = () => {
      if (pending.cancelled || token !== this.#variantUpdateToken) return;
      if (!img.isConnected || img.getAttribute('data_max_resolution') !== maxRes) return;
      this.#applyImageSource(img, maxRes);
      img.dataset.highResApplied = 'true';
      if (this.#pendingUpgrade === pending) this.#pendingUpgrade = null;
    };
    loader.onerror = () => {
      if (this.#pendingUpgrade === pending) this.#pendingUpgrade = null;
    };
    loader.src = maxRes;
  }

  #ensurePrimaryImageResolution() {
    const isDesktop = window.matchMedia('(min-width: 1041px)').matches;

    if (isDesktop && this.presentation === 'stack') {
      const firstGridImage = this.querySelector(
        '.media-gallery__grid .product-media-container:first-child img.product-media__image'
      );
      this.#upgradeImageToMaxResolution(firstGridImage);
      return;
    }

    const primaryImage = this.#getPrimaryImage();
    if (primaryImage instanceof HTMLImageElement) {
      this.#upgradeImageToMaxResolution(primaryImage);
    }
  }

  #previewVariantMedia(variant) {
    if (this.dataset.hideVariants !== 'true') return;

    const mediaId = variant.featured_media?.id;
    const previewUrl = this.#getPreviewVariantUrl(variant);
    if (!previewUrl) return;

    const images = [];
    const gridImg = this.querySelector(
      '.media-gallery__grid .product-media-container:first-child img.product-media__image'
    );
    const slideImg = this.querySelector(
      'slideshow-component slideshow-slide:first-of-type img.product-media__image'
    );
    if (gridImg) images.push(gridImg);
    if (slideImg && slideImg !== gridImg) images.push(slideImg);
    if (images.length === 0) {
      const fallback = this.#getPrimaryImage();
      if (fallback instanceof HTMLImageElement) images.push(fallback);
    }

    images.forEach((img) => {
      const mediaEl = img.closest('.product-media');
      if (mediaEl && mediaId && mediaEl.getAttribute('data-media-id') === String(mediaId)) {
        return;
      }
      if (mediaEl && mediaId) mediaEl.setAttribute('data-media-id', String(mediaId));
      this.#applyImageSource(img, previewUrl, variant.featured_media?.alt);
    });

    const slideshow = this.slideshow;
    if (slideshow) {
      slideshow.select(0, undefined, { animate: false });
    }
  }

  #findMediaSlide(slides, mediaIdString) {
    return Array.from(slides).find((slide) => {
      return (
        slide.getAttribute('slide-id') === mediaIdString ||
        Boolean(slide.querySelector(`[data-media-id="${mediaIdString}"]`))
      );
    });
  }

  #switchToVariantMedia(variant) {
    if (this.dataset.hideVariants === 'true') return false;

    const mediaId = variant?.featured_media?.id;
    if (!mediaId) return false;

    const mediaIdString = String(mediaId);
    let switched = false;

    const grid = this.querySelector('.media-gallery__grid');
    if (grid) {
      const target = grid.querySelector(`.product-media[data-media-id="${mediaIdString}"]`);
      const container = target?.closest('.product-media-container');
      if (container && container.parentElement === grid) {
        if (container !== grid.firstElementChild) {
          grid.insertBefore(container, grid.firstElementChild);
        }
        switched = true;
      }
    }

    const slideshow = this.slideshow;
    const slides = slideshow ? slideshow.querySelectorAll('slideshow-slide') : [];

    if (slideshow && slides.length > 0) {
      const targetSlide = this.#findMediaSlide(slides, mediaIdString);
      if (targetSlide) {
        const index = Array.from(slides).indexOf(targetSlide);
        if (index >= 0) {
          slideshow.select(index, undefined, { animate: false });
          switched = true;
        }
      }
    }

    return switched;
  }

  #handleZoomMediaSelected = async (event) => {
    if (this.slideshow) this.slideshow.select(event.detail.index, undefined, { animate: false });
  };

  zoom(index, event) {
    if (this.refs.zoomDialogComponent) this.refs.zoomDialogComponent.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
